import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import { darken } from '@material-ui/core/styles/colorManipulator';

import MaterialButtonGroup from '@material-ui/core/ButtonGroup';
import InputLabel from '@material-ui/core/InputLabel';
import Button from '../Button';

const styles = theme => ({
  root: {
    '& .MuiButtonGroup-groupedContainedHorizontal': {
      borderRadius: theme.radius.sm,
    },
    display: 'flex',
    alignItems: 'center',
  },
  group: {
    '& > .MuiInputBase-root': {
      background: theme.palette.common.white,
      borderRadius: theme.radius.md,
      marginTop: theme.spacing(2.5),
      '& > .Mui-disabled': {
        background: darken(theme.palette.common.white, 0.2),
      },
      paddingLeft: theme.spacing(1.5),
    },
    padding: theme.spacing(0, 0, 0, 1),
    boxShadow: 'none',
  },
  rootNoLabel: {
    '& > .MuiInputBase-root': {
      marginTop: 0,
    },
  },
  inputLabel: {
    '&.Mui-focused': {
      color: theme.palette.common.white,
    },
    '&.MuiInputLabel-shrink': {
      color: theme.palette.common.white,
    },
    color: theme.palette.primary.main,
    fontSize: theme.typography.h4.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    margin: 0,
    top: theme.spacing(0.25),
    flex: '1 0 0',
    zIndex: 10, // Raise it above the InputBase that is rendered after it.
  },
  menuItem: {
    marginRight: theme.spacing(1),
    fontSize: '10px',
  },
  unselectedButton: {
    opacity: 0.5,
  },
});

const COLOR_MAP = {
  low: 'primary',
  medium: 'orange',
  high: 'red',
};

function PriorityButtonGroup({
  classes,
  className,
  label,
  showLabel,
  options,
  onChange,
  value,
  selectId,
  LabelProps,
  SelectProps,
  variant: propVariant,
  colorMap,
  onlyFillSelected,
  ...rest
}) {
  const applyButtonColor = (option, currentValue) => {
    if (onlyFillSelected) {
      return currentValue === option.value
        ? COLOR_MAP[option.value] || null
        : 'primaryGray';
    }
    return colorMap[option.value];
  };

  return (
    <div className={classNames(classes.root, className)}>
      {showLabel && (
        <InputLabel
          className={classes.inputLabel}
          htmlFor={selectId}
          id={`${selectId}-label`}
          {...LabelProps}
        >
          {label}
        </InputLabel>
      )}
      <MaterialButtonGroup
        variant="contained"
        size="small"
        className={classes.group}
        {...rest}
      >
        {options.map(option => (
          <Button
            className={classNames(classes.menuItem, {
              [classes.unselectedButton]: !onlyFillSelected && option.value !== value,
            })}
            key={option.value}
            value={option.value}
            color={applyButtonColor(option, value)}
            onClick={() => onChange({ target: { value: option.value } })}
            {...rest}
            variant={propVariant || value === option.value ? 'contained' : 'outlined'}
          >
            {option.name}
          </Button>
        ))}
      </MaterialButtonGroup>
    </div>
  );
}

PriorityButtonGroup.defaultProps = {
  classes: {},
  className: '',
  showLabel: true,
  variant: null,
  colorMap: COLOR_MAP,
  onlyFillSelected: true,
};

PriorityButtonGroup.propTypes = {
  classes: PropTypes.object,
  className: PropTypes.string,
  showLabel: PropTypes.bool,
  variant: PropTypes.string,
  colorMap: PropTypes.object,
  onlyFillSelected: PropTypes.bool,
};

export default withStyles(styles, { name: 'ButtonGroup' })(PriorityButtonGroup);
